import React from 'react';
import { Box, Link, Typography, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import MicIcon from '@mui/icons-material/Mic';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { formatData, formatValuta } from '@gestionale/core/i18n/formattazione';
import type { ContestoLocale } from '@gestionale/core/i18n/lingue';
import { GATTO_SAGOMA, GATTO_VIEWBOX, LILLA, VIOLA_NOTTE } from '@shared/yuma-sito/gatto';
import type { MessaggioMostrato, SchedaDiYuma } from '@shared/yuma-sito/tipi';
import { tokens } from '../../theme';

/**
 * La conversazione com'è stata vista dal visitatore (30/10/2026): le sue frasi a destra, scure; quelle di Yuma a
 * sinistra col gatto, senza fumetto, come nella pagina del sito. Le schede (conto, pagine, video, marchio, modulo)
 * sono riassunte in riquadri leggibili, senza ricostruire la pagina pubblica: qui serve capire cosa è successo.
 * Tutto il testo arriva dal visitatore o dal modello: si mostra SOLO come testo (React lo protegge), e i soli
 * collegamenti cliccabili sono quelli verso yumaos.com.
 */
export const GattoPiccolo: React.FC<{ lato?: number }> = ({ lato = 28 }) => (
  <Box sx={{ width: lato, height: lato, flexShrink: 0, borderRadius: '50%', bgcolor: VIOLA_NOTTE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg width={Math.round(lato * 0.58)} height={Math.round(lato * 0.58)} viewBox={GATTO_VIEWBOX} aria-hidden focusable="false">
      <path d={GATTO_SAGOMA} fill={LILLA} fillRule="evenodd" />
    </svg>
  </Box>
);

/** Solo gli indirizzi del nostro sito diventano collegamenti; tutto il resto resta testo. */
function delNostroSito(indirizzo: string): boolean {
  try {
    const u = new URL(indirizzo);
    return u.protocol === 'https:' && (u.hostname === 'yumaos.com' || u.hostname === 'www.yumaos.com');
  } catch {
    return false;
  }
}

const riquadro = {
  border: `1px solid ${tokens.color.line}`,
  borderRadius: `${tokens.radius.md}px`,
  bgcolor: tokens.color.paper,
  px: 1.5,
  py: 1.25,
} as const;

const SchedaRiassunta: React.FC<{ scheda: SchedaDiYuma; ctx: ContestoLocale }> = ({ scheda, ctx }) => {
  const { t } = useTranslation('yumaSito');
  const etichetta = (testo: string) => (
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: tokens.color.ash, mb: 0.5 }}>{testo}</Typography>
  );
  switch (scheda.tipo) {
    case 'video':
      return (
        <Box sx={riquadro}>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.color.graphite }}>
            {t('schede.video', { slug: scheda.slug, secondo: Math.floor(scheda.da_secondo) })}
          </Typography>
        </Box>
      );
    case 'pagina':
      return (
        <Box sx={riquadro}>
          {etichetta(t('schede.pagina'))}
          {delNostroSito(scheda.indirizzo) ? (
            <Link href={scheda.indirizzo} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: '0.9rem' }}>
              {scheda.titolo}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          ) : (
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{scheda.titolo}</Typography>
          )}
        </Box>
      );
    case 'conto':
      return (
        <Box sx={riquadro}>
          {etichetta(`${t('schede.conto')}, ${t(`schede.${scheda.conto.periodo}`)}`)}
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              {scheda.conto.righe.map((r) => (
                <tr key={r.voce}>
                  <Box component="td" sx={{ py: 0.25, color: tokens.color.graphite }}>{r.voce}</Box>
                  <Box component="td" sx={{ py: 0.25, px: 1, color: tokens.color.ash, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {`${r.quantita} × ${formatValuta(r.prezzo_unitario, ctx)}`}
                  </Box>
                  <Box component="td" sx={{ py: 0.25, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatValuta(r.totale, ctx)}</Box>
                </tr>
              ))}
              <Box component="tr" sx={{ borderTop: `1px solid ${tokens.color.line}` }}>
                <Box component="td" colSpan={2} sx={{ pt: 0.75, fontWeight: 700 }}>{t('schede.totale')}</Box>
                <Box component="td" sx={{ pt: 0.75, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatValuta(scheda.conto.totale, ctx)}</Box>
              </Box>
            </tbody>
          </Box>
        </Box>
      );
    case 'marchio':
      return (
        <Box sx={riquadro}>
          {etichetta(t('schede.marchio'))}
          <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{scheda.marchio.nome ?? scheda.marchio.indirizzo}</Typography>
          <Typography sx={{ fontSize: '0.8rem', color: tokens.color.ash, wordBreak: 'break-all' }}>{scheda.marchio.indirizzo}</Typography>
          {scheda.marchio.colori.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
              {scheda.marchio.colori.filter((c) => /^#[0-9a-f]{3,8}$/i.test(c)).map((c) => (
                <Box key={c} title={c} sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: c, border: `1px solid ${alpha('#000', 0.1)}` }} />
              ))}
            </Box>
          )}
        </Box>
      );
    case 'iscrizione':
      return (
        <Box sx={{ ...riquadro, bgcolor: alpha(VIOLA_NOTTE, 0.04), borderColor: alpha(VIOLA_NOTTE, 0.18) }}>
          {etichetta(t('schede.modulo'))}
          <Typography sx={{ fontSize: '0.85rem', color: tokens.color.graphite }}>
            {[scheda.contatti.nome, scheda.contatti.azienda, scheda.contatti.email].filter(Boolean).join(', ')}
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: tokens.color.ash }}>
            {t('schede.risposte', { count: Object.keys(scheda.risposte ?? {}).length })}
          </Typography>
        </Box>
      );
  }
};

export const Trascrizione: React.FC<{ messaggi: MessaggioMostrato[]; ctx: ContestoLocale }> = ({ messaggi, ctx }) => {
  const { t } = useTranslation('yumaSito');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {messaggi.map((m, i) =>
        m.ruolo === 'persona' ? (
          <Box key={i} sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
            <Box sx={{ px: 1.75, py: 1, borderRadius: '16px 16px 4px 16px', bgcolor: tokens.color.graphite, color: '#fff' }}>
              <Typography sx={{ fontSize: '0.925rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{m.testo}</Typography>
            </Box>
            <Typography sx={{ mt: 0.4, fontSize: '0.7rem', color: tokens.color.mist, textAlign: 'right', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
              {m.a_voce && (
                <>
                  <MicIcon sx={{ fontSize: 12 }} aria-hidden />
                  {t('dettaglio.detto')}
                  {', '}
                </>
              )}
              {formatData(m.il, ctx, 'oraSola')}
            </Typography>
          </Box>
        ) : (
          <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', maxWidth: '92%' }}>
            <GattoPiccolo />
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ fontSize: '0.925rem', lineHeight: 1.6, color: tokens.color.graphite, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', pt: 0.25 }}>
                {m.testo}
              </Typography>
              {(m.schede ?? []).map((s, k) => (
                <SchedaRiassunta key={k} scheda={s} ctx={ctx} />
              ))}
            </Box>
          </Box>
        ),
      )}
    </Box>
  );
};
